import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { mediaService } from "../../services/media";
import { getApiErrorMessage } from "../../utils/axios";
import { sitePath } from "../../utils/urls";

const DRAWER_MS = 300;

const MEDIA_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "logo", label: "Logo" },
  { value: "favicon", label: "Favicon" },
  { value: "meta_image", label: "Meta image" },
  { value: "meta_video", label: "Meta video" },
];

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaDetailDrawer = ({ project, item, onSaved, showCost = false }) => {
  const navigate = useNavigate();
  const { siteSlug } = useParams();
  const versionInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [mediaType, setMediaType] = useState(item?.media_type || "");
  const [detail, setDetail] = useState(item);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTitle(item?.title || "");
    setDescription(item?.description || "");
    setMediaType(item?.media_type || "");
    setDetail(item);
    setPreviewVersion(null);
  }, [item?.hash, item?.title, item?.description, item?.media_type, item]);

  useEffect(() => {
    if (!project?.hash || !item?.hash) return;
    if (item.versions) {
      setDetail(item);
      return;
    }
    mediaService.get(
      project.hash,
      item.hash,
      (response) => setDetail(response.data),
      () => {},
    );
  }, [project?.hash, item?.hash, item]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Mount preview only after the slide-in finishes; drop it before slide-out.
  useEffect(() => {
    if (!open) {
      setMediaReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setMediaReady(true), DRAWER_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const close = () => {
    setMediaReady(false);
    setOpen(false);
    window.setTimeout(() => navigate(sitePath(siteSlug, "media/")), DRAWER_MS);
  };

  const save = (event) => {
    event.preventDefault();
    if (!project || !item) return;
    setSaving(true);
    setError("");
    mediaService.update(
      project.hash,
      item.hash,
      { title, description, media_type: mediaType },
      (response) => {
        setSaving(false);
        setDetail(response.data);
        onSaved?.(response.data);
        close();
      },
      (err) => {
        setSaving(false);
        setError(getApiErrorMessage(err, "Could not save."));
      },
    );
  };

  const onNewVersion = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !project || !item) return;
    setUploadingVersion(true);
    setError("");
    mediaService.uploadVersion(
      project.hash,
      item.hash,
      file,
      {},
      (response) => {
        setUploadingVersion(false);
        setDetail(response.data);
        setPreviewVersion(null);
        onSaved?.(response.data);
      },
      (err) => {
        setUploadingVersion(false);
        setError(getApiErrorMessage(err, "Could not upload version."));
      },
    );
  };

  if (!item) return null;

  const versions = detail?.versions || [];
  const active = previewVersion || null;
  const previewUrl = active?.url || detail?.url || item.url;
  const previewContentType = active?.content_type || detail?.content_type || item.content_type;
  const isVideo = (previewContentType || "").startsWith("video/");
  const cost = active ? active.cost_usd : detail?.cost_usd;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close media detail"
        onClick={close}
        className={`absolute inset-0 bg-theme-text transition-opacity duration-300 ease-out ${
          open ? "opacity-25" : "opacity-0"
        }`}
      />
      <aside
        className={`absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-theme-secondary bg-theme-main shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-theme-secondary px-5 py-4">
          <h2 className="font-semibold text-theme-text">Media</h2>
          <button
            type="button"
            onClick={close}
            className="rounded-lg px-2 py-1 text-sm text-theme-text opacity-60 hover:bg-black hover:bg-opacity-5 hover:opacity-100"
          >
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 flex aspect-video items-center justify-center overflow-hidden rounded-lg bg-theme-secondary">
            {mediaReady && isVideo ? (
              <video src={previewUrl} controls className="max-h-full max-w-full" />
            ) : mediaReady ? (
              <img src={previewUrl} alt={item.title || ""} className="max-h-full max-w-full object-contain" />
            ) : null}
          </div>
          {versions.length > 0 && (
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-theme-text opacity-60">Versions</span>
                <button
                  type="button"
                  disabled={uploadingVersion}
                  onClick={() => versionInputRef.current?.click()}
                  className="text-xs font-semibold text-theme-primary hover:opacity-80 disabled:opacity-50"
                >
                  {uploadingVersion ? "Uploading…" : "New version"}
                </button>
                <input
                  ref={versionInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.ico,.mov,.mp4"
                  className="hidden"
                  onChange={onNewVersion}
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {versions.map((v) => {
                  const isLatest = v.version_number === detail?.version_number && !previewVersion;
                  const isActive = previewVersion
                    ? previewVersion.hash === v.hash
                    : isLatest;
                  return (
                    <button
                      key={v.hash}
                      type="button"
                      onClick={() => {
                        if (v.version_number === detail?.version_number) setPreviewVersion(null);
                        else setPreviewVersion(v);
                      }}
                      className={`rounded border px-2 py-1 text-[11px] font-mono ${
                        isActive
                          ? "border-theme-primary text-theme-text"
                          : "border-theme-secondary text-theme-text opacity-60 hover:opacity-100"
                      }`}
                    >
                      v{v.version_number}
                    </button>
                  );
                })}
              </div>
              {previewVersion && (
                <p className="mt-1 text-[11px] text-theme-text opacity-50">
                  Viewing v{previewVersion.version_number} (site still uses latest)
                </p>
              )}
            </div>
          )}
          <form onSubmit={save} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-theme-text opacity-60">Title</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-theme-text opacity-60">Description</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-theme-text opacity-60">Media type</span>
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value)}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text"
              >
                {MEDIA_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <dl className="space-y-1 text-xs text-theme-text opacity-70">
              <div className="flex justify-between gap-2">
                <dt>Path</dt>
                <dd className="font-mono">{detail?.path || item.path}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Type</dt>
                <dd>{previewContentType || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Size</dt>
                <dd>{formatBytes(active?.size ?? detail?.size ?? item.size)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Dimensions</dt>
                <dd>
                  {(active?.width || detail?.width || item.width)
                    && (active?.height || detail?.height || item.height)
                    ? `${active?.width || detail?.width || item.width} × ${active?.height || detail?.height || item.height}`
                    : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt>Source</dt>
                <dd>{active?.source || detail?.source || item.source}</dd>
              </div>
              {showCost && cost != null && (
                <div className="flex justify-between gap-2">
                  <dt>Cost</dt>
                  <dd>${Number(cost).toFixed(4)}</dd>
                </div>
              )}
              {(active?.prompt || detail?.prompt || item.prompt) ? (
                <div>
                  <dt className="mb-1">Prompt</dt>
                  <dd className="rounded bg-theme-secondary p-2 text-theme-text opacity-90">
                    {active?.prompt || detail?.prompt || item.prompt}
                  </dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-2">
                <dt>Created</dt>
                <dd>
                  {(active?.create_date || detail?.create_date || item.create_date)
                    ? new Date(active?.create_date || detail?.create_date || item.create_date).toLocaleString()
                    : "—"}
                </dd>
              </div>
            </dl>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                className="flex-1 rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-5"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </aside>
    </div>
  );
};

export default MediaDetailDrawer;
